import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import metricEntriesAPI from '@/api/metricEntries';
import { normalizeData, normalizeIncluded } from '@/utils/jsonapi';

// Create entity adapter
const metricEntriesAdapter = createEntityAdapter({
  selectId: (entry) => entry.id,
  sortComparer: (a, b) => (b.entry_date || '').localeCompare(a.entry_date || ''),
});

/**
 * Async thunk to fetch all metric entries
 * Normalizes JSON:API response before storing in Redux
 */
export const fetchMetricEntriesThunk = createAsyncThunk(
  'metricEntries/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricEntriesAPI.getAll(params);

      // Detect grouped response (server-side collapsing)
      const isGrouped = jsonApiResponse.meta?.pagination?.grouped === true;

      if (isGrouped) {
        // Grouped data still goes through JSON:API renderer — normalize it
        const normalized = normalizeData(jsonApiResponse.data) || [];
        const groupedData = (Array.isArray(normalized) ? normalized : [normalized]).map((item) => ({
          ...item,
          _isGrouped: true,
          _entryCount: item._entry_count || 1,
        }));
        return {
          data: groupedData,
          meta: jsonApiResponse.meta,
          links: jsonApiResponse.links,
        };
      }

      // Process included resources (partition values, partition types, users, etc.)
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize JSON:API format to flat objects, resolving each entry's
      // external_actor relationship (EG-1: who submitted this through the
      // public embed form) into its full attributes - normalizeData leaves
      // relationships as bare {type, id} refs, and the entries table needs
      // display_name to render "<display_name> · Rock".
      const entries = (normalizeData(jsonApiResponse.data) || []).map((entry) => ({
        ...entry,
        external_actor: entry.external_actor
          ? included.external_actor?.[entry.external_actor.id] || entry.external_actor
          : null,
      }));

      return {
        data: entries,
        meta: jsonApiResponse.meta,
        links: jsonApiResponse.links
      };
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to fetch a single metric entry by ID
 * Normalizes JSON:API response before storing in Redux
 */
export const fetchMetricEntryDetailThunk = createAsyncThunk(
  'metricEntries/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricEntriesAPI.getById(id);
      // Normalize single resource
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to create a new metric entry
 * Normalizes JSON:API response before storing in Redux
 */
export const createMetricEntryThunk = createAsyncThunk(
  'metricEntries/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricEntriesAPI.create(data);
      // Normalize single resource
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      // `code` carries the backend's error code (e.g. BE-5's 409
      // `metric_entry_locked`) alongside the human message, so a caller that
      // needs to react to a specific failure - not just display it - can
      // (see useMetricGroupEntry.js). Keeping `message` as the primary field
      // means any caller that only reads `.message` (MetricEntryForm.jsx)
      // still works unchanged.
      return rejectWithValue({
        message: error.apiError?.message || error.message,
        code: error.apiError?.code,
      });
    }
  }
);

/**
 * Async thunk to update an existing metric entry
 * Normalizes JSON:API response before storing in Redux
 */
export const updateMetricEntryThunk = createAsyncThunk(
  'metricEntries/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricEntriesAPI.update(id, data);
      // Normalize single resource
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      // See createMetricEntryThunk above for why this is {message, code}.
      return rejectWithValue({
        message: error.apiError?.message || error.message,
        code: error.apiError?.code,
      });
    }
  }
);

/**
 * Async thunk to delete a metric entry
 */
export const deleteMetricEntryThunk = createAsyncThunk(
  'metricEntries/delete',
  async (id, { rejectWithValue }) => {
    try {
      await metricEntriesAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

// Slice
const metricEntriesSlice = createSlice({
  name: 'metricEntries',
  initialState: metricEntriesAdapter.getInitialState({
    currentEntry: null,
    loading: false,
    error: null,
    // Only the entries-list fetch sets this: a failed create/update/delete
    // (state.error) must not replace the list with a load-failure state.
    listError: null,
    pagination: null,  // { count, page, pages, page_size }
  }),
  reducers: {
    setCurrentEntry: (state, action) => {
      state.currentEntry = action.payload;
    },
    clearEntries: (state) => {
      metricEntriesAdapter.removeAll(state);
    },
    upsertMany: (state, action) => {
      metricEntriesAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Entries
      .addCase(fetchMetricEntriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.listError = null;
      })
      .addCase(fetchMetricEntriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.listError = null;
        metricEntriesAdapter.setAll(state, action.payload?.data || []);
        state.pagination = action.payload?.meta?.pagination || null;
      })
      .addCase(fetchMetricEntriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.listError = action.payload || action.error?.message || 'Request failed';
      })
      // Fetch Detail
      .addCase(fetchMetricEntryDetailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMetricEntryDetailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntry = action.payload;
      })
      .addCase(fetchMetricEntryDetailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createMetricEntryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMetricEntryThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricEntriesAdapter.addOne(state, action.payload);
      })
      .addCase(createMetricEntryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateMetricEntryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMetricEntryThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricEntriesAdapter.upsertOne(state, action.payload);
        if (state.currentEntry?.id === action.payload.id) {
          state.currentEntry = action.payload;
        }
      })
      .addCase(updateMetricEntryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteMetricEntryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMetricEntryThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricEntriesAdapter.removeOne(state, action.payload);
        if (state.currentEntry?.id === action.payload) {
          state.currentEntry = null;
        }
      })
      .addCase(deleteMetricEntryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentEntry, clearEntries, upsertMany } = metricEntriesSlice.actions;

// Entity adapter selectors
export const {
  selectAll: selectAllMetricEntries,
  selectById: selectMetricEntryById,
  selectIds: selectMetricEntryIds,
} = metricEntriesAdapter.getSelectors((state) => state.metricEntries || metricEntriesAdapter.getInitialState());

// Additional selectors
export const selectMetricEntries = selectAllMetricEntries; // Alias for backward compatibility
export const selectCurrentEntry = (state) => state.metricEntries?.currentEntry || null;
export const selectMetricEntriesLoading = (state) => state.metricEntries?.loading || false;
export const selectMetricEntriesError = (state) => state.metricEntries?.error || null;
/** Why the last entries-list load failed, or null; create/update/delete failures never set it. */
export const selectMetricEntriesListError = (state) => state.metricEntries?.listError || null;
export const selectMetricEntriesPagination = (state) => state.metricEntries?.pagination || null;

export default metricEntriesSlice.reducer;
